import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import React, { useContext, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Trans, useTranslation } from 'react-i18next';
import { URLS } from '@wix/da-url';
import MessageModal from '@wix/da-ds/pkg/modals/dsPresets/MessageModal';
import { CancelButton, ConfirmButton } from '@wix/da-ds/pkg/modals/dsPresets/MessageModal/buttons';
import LoadingIndicator from '@wix/da-ds/pkg/LoadingIndicator';
import BoostIllustration from '../BoostIllustration';
import { makeXHRCall } from '../../../../../simpleXHR/actionCreators';
import { SimpleXHREndpoint, SimpleXHRMethod } from '../../../../../simpleXHR/constants';
import { ResourceType } from '../../../../../types/resource';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import { addErrorNotification } from '../../../../../Notifications/redux/actionCreators';
import { putEntities } from '../../../../../redux/entities/actions';
import { normalizeDeviation } from '../../../../../redux/normalizr/helpers';
import { useBiLogger } from '@wix/da-bi/pkg/hooks/useBiLogger';
import BiLink from '@wix/da-bi/pkg/Components/BiLink';
import { BiContextProvidedValue, BiData } from '@wix/da-bi/pkg/events';
import { ReferralType } from '@wix/da-bi/pkg/constants';
import { SessionContext } from '@wix/da-react-context/pkg/publicSession';
import s from './BoostModal.scss';
const BoostModal = ({ deviation, onSuccess, onRequestClose })=>{
    var _deviation_extended;
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const isMobile = useContext(MobileContext);
    const { coreLevel } = useContext(SessionContext);
    const [isBusy, setIsBusy] = useState(false);
    const logBiEvent = useBiLogger();
    const isTier = !!deviation.tier;
    const { canUseFreeBoost, freeBoostsRemaining = 1, freeBoostsAllowed = 3, boostPointsPrice = 0, pointsBalance = 0 } = ((_deviation_extended = deviation.extended) === null || _deviation_extended === void 0 ? void 0 : _deviation_extended.boostRestrictions) || {};
    const remainingBoostsString = freeBoostsAllowed > 1 ? `${freeBoostsRemaining} of ${freeBoostsAllowed}` : freeBoostsRemaining;
    const handleConfirm = ()=>{
        var _deviation_author;
        setIsBusy(true);
        if (!canUseFreeBoost && pointsBalance < boostPointsPrice) {
            logBiEvent(BiData({
                evid: 504,
                typeid: deviation.typeId,
                widgetname: 'boost_modal'
            }));
            window.location.href = URLS.POINTS;
            return;
        }
        logBiEvent(BiData({
            evid: 321,
            badge_type: 'boost',
            badge_typeid: 176,
            sectionname: BiContextProvidedValue,
            itemid: deviation.deviationId,
            typeid: deviation.typeId
        }));
        dispatch(makeXHRCall({
            endpoint: SimpleXHREndpoint.GIVE_BADGE,
            method: SimpleXHRMethod.POST,
            data: {
                typeid: ResourceType.DEVIATION,
                itemid: deviation.deviationId,
                userid: (_deviation_author = deviation.author) === null || _deviation_author === void 0 ? void 0 : _deviation_author.userId,
                badgetype: 'Boost',
                use_points: !canUseFreeBoost
            },
            onSuccess: ()=>{
                const entities = normalizeDeviation(_object_spread_props(_object_spread({}, deviation), {
                    boosted: true,
                    extended: _object_spread_props(_object_spread({}, deviation.extended), {
                        boosted: true
                    })
                }));
                dispatch(putEntities({
                    entities
                }));
                onSuccess();
            },
            onError: ()=>{
                dispatch(addErrorNotification(t('common.somethingWentWrong')));
            }
        }));
    };
    const renderUpgradeCoreLink = ()=>{
        if (coreLevel === 'core_pro_plus') {
            return null;
        }
        return /*#__PURE__*/ React.createElement(BiLink, {
            biData: BiData({
                evid: 375,
                referral_info: ReferralType.BOOST_DEVIATION_POPUP
            }),
            href: URLS.CORE_MEMBERSHIP,
            className: "link"
        }, "Upgrade your plan");
    };
    const renderMessage = ()=>/*#__PURE__*/ React.createElement("div", {
            className: s['message']
        }, /*#__PURE__*/ React.createElement("div", null, /*#__PURE__*/ React.createElement(Trans, {
            i18nKey: `boostDeviation.modal.message${isTier ? '.tier' : ''}`
        }, "Your deviation will be more visible in browse for the next 48 hours.", ' ', /*#__PURE__*/ React.createElement("a", {
            className: "link",
            target: "_blank",
            href: t('boostDeviation.learnMoreUrl'),
            rel: "noreferrer"
        }, "Learn more"))), /*#__PURE__*/ React.createElement("br", null), canUseFreeBoost ? /*#__PURE__*/ React.createElement(React.Fragment, null, /*#__PURE__*/ React.createElement("div", null, /*#__PURE__*/ React.createElement(Trans, {
            i18nKey: "boostDeviation.modal.remainingFreeBoosts",
            values: {
                remainingBoostsString
            }
        }, "You have ", /*#__PURE__*/ React.createElement("strong", null, remainingBoostsString), " free boost this month. ", renderUpgradeCoreLink()))) : /*#__PURE__*/ React.createElement(React.Fragment, null, /*#__PURE__*/ React.createElement("div", null, /*#__PURE__*/ React.createElement(Trans, {
            i18nKey: "boostDeviation.modal.noFreeBoost"
        }, "You have ", /*#__PURE__*/ React.createElement("strong", null, "no remaining free boosts"), " this month.", ' ', renderUpgradeCoreLink())), /*#__PURE__*/ React.createElement("div", null, /*#__PURE__*/ React.createElement(Trans, {
            i18nKey: "boostDeviation.modal.pointsBalance",
            count: pointsBalance
        }, "Balance: ", {
            pointsBalance
        }, " Points.", ' ', /*#__PURE__*/ React.createElement(BiLink, {
            biData: BiData({
                evid: 504,
                typeid: deviation.typeId,
                widgetname: 'boost_modal'
            }),
            href: URLS.POINTS,
            className: "link"
        }, "Get more")))));
    if (!deviation.extended) {
        return /*#__PURE__*/ React.createElement(LoadingIndicator, {
            view: "llama",
            usePortal: true,
            withOverlay: true
        });
    }
    return /*#__PURE__*/ React.createElement(MessageModal, {
        isOpen: true,
        onRequestClose: onRequestClose,
        image: /*#__PURE__*/ React.createElement(BoostIllustration, null),
        variant: isMobile ? 'vertical' : 'horizontal',
        contentClassName: s['content'],
        title: t(`boostDeviation.modal.title${isTier ? '.tier' : ''}`),
        message: renderMessage(),
        footerButtons: [
            /*#__PURE__*/ React.createElement(CancelButton, {
                key: "cancel",
                onClick: onRequestClose
            }, t('popup.cancel')),
            /*#__PURE__*/ React.createElement(ConfirmButton, {
                loading: isBusy,
                key: "confirm",
                onClick: handleConfirm
            }, canUseFreeBoost ? t('boostDeviation.modal.button.boostNow') : t('boostDeviation.modal.button.boostForPoints', {
                boostPointsPrice
            }))
        ]
    });
};
BoostModal.displayName = 'BoostModal';
export default BoostModal;

//# sourceMappingURL=BoostModal.js.map